/**
 * 📅 LogCalendar — shared date picker across Food / Gym / Cardio logs.
 *
 * Feb 2026 (owner drop): "Please add a calendar in the (GYM/FOOD/WORKOUT logs)
 * that also connects to each other. This will help add missed days and future days."
 *
 * Usage:
 *   <LogCalendar
 *     selectedDate={selectedDate}
 *     onSelectDate={setSelectedDate}
 *     dotsByDate={{ "2026-02-14": ["food","gym"], ... }}
 *   />
 *
 * - Month grid, click a day to select (past, present, or future).
 * - Colored dots per stream on days with entries (food=amber, gym=green, cardio=cyan).
 * - "Today" quick-jump button.
 * - Selected date pulses.
 * - PT-timezone aware to match todayKey() semantics used across log components.
 */
import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const STREAM_COLORS = {
  food:   "#FBBF24",   // amber — matches FoodLog theme
  gym:    "#22C55E",   // green — MuscleTracker
  cardio: "#22D3EE",   // cyan — CardioTracker
};

const STREAM_LABELS = {
  food:   "Food",
  gym:    "Gym",
  cardio: "Cardio",
};

// PT date helper — must match todayKey() in FoodLog / MuscleTracker
function todayKeyPT() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
}

function pad(n) { return String(n).padStart(2, "0"); }

function dateKey(y, m, d) {
  return `${y}-${pad(m + 1)}-${pad(d)}`;
}

// Return array of 42 cells (6 weeks) with year/month/day/inMonth flags
function buildMonthGrid(year, month) {
  const first = new Date(Date.UTC(year, month, 1));
  const startWeekday = first.getUTCDay();               // 0 = Sunday
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const prevMonthDays = new Date(Date.UTC(year, month, 0)).getUTCDate();

  const cells = [];
  // Trailing days from prev month
  for (let i = startWeekday - 1; i >= 0; i--) {
    const d = prevMonthDays - i;
    const py = month === 0 ? year - 1 : year;
    const pm = month === 0 ? 11 : month - 1;
    cells.push({ y: py, m: pm, d, inMonth: false });
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ y: year, m: month, d, inMonth: true });
  }
  // Leading days from next month to fill 42
  let next = 1;
  while (cells.length < 42) {
    const ny = month === 11 ? year + 1 : year;
    const nm = month === 11 ? 0 : month + 1;
    cells.push({ y: ny, m: nm, d: next++, inMonth: false });
  }
  return cells;
}

const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function LogCalendar({
  selectedDate,
  onSelectDate,
  dotsByDate = {},         // { "YYYY-MM-DD": ["food","gym","cardio"] }
  streams = ["food", "gym", "cardio"],  // which legend items to show
  compact = false,
}) {
  const today = todayKeyPT();
  const initial = useMemo(() => {
    const src = selectedDate || today;
    const [y, m] = src.split("-").map(Number);
    return { year: y, month: m - 1 };
  }, [selectedDate, today]);
  const [viewYear, setViewYear] = useState(initial.year);
  const [viewMonth, setViewMonth] = useState(initial.month);

  const cells = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const gotoPrev = () => {
    if (viewMonth === 0) { setViewYear(viewYear - 1); setViewMonth(11); }
    else setViewMonth(viewMonth - 1);
  };
  const gotoNext = () => {
    if (viewMonth === 11) { setViewYear(viewYear + 1); setViewMonth(0); }
    else setViewMonth(viewMonth + 1);
  };
  const gotoToday = () => {
    const [y, m] = today.split("-").map(Number);
    setViewYear(y); setViewMonth(m - 1);
    onSelectDate?.(today);
  };

  return (
    <div
      className={`bg-neutral-900/90 border border-neutral-700 rounded-lg ${compact ? "p-2" : "p-3 sm:p-4"} text-neutral-100`}
      data-testid="log-calendar"
    >
      {/* Header — month nav */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={gotoPrev}
          className="p-1 rounded hover:bg-neutral-800 text-neutral-300"
          data-testid="log-calendar-prev"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 text-sm font-semibold">
          <CalendarDays className="w-4 h-4 text-[#F1C84B]" />
          <span data-testid="log-calendar-title">
            {MONTH_LABELS[viewMonth]} {viewYear}
          </span>
        </div>
        <button
          onClick={gotoNext}
          className="p-1 rounded hover:bg-neutral-800 text-neutral-300"
          data-testid="log-calendar-next"
          aria-label="Next month"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Weekday labels */}
      <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] text-neutral-500 text-center font-mono uppercase tracking-widest">
        {WEEKDAY_LABELS.map((d, i) => (
          <div key={i}>{d}</div>
        ))}
      </div>

      {/* Month grid */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          const key = dateKey(c.y, c.m, c.d);
          const isToday = key === today;
          const isSelected = key === selectedDate;
          const isFuture = key > today;
          const dots = dotsByDate[key] || [];
          return (
            <button
              key={i}
              onClick={() => onSelectDate?.(key)}
              data-testid={`log-calendar-day-${key}`}
              className={`relative aspect-square rounded flex flex-col items-center justify-center text-[11px] transition-colors
                ${c.inMonth ? "text-neutral-100" : "text-neutral-600"}
                ${isSelected ? "bg-[#F1C84B] text-black font-bold ring-2 ring-[#F1C84B]" : "hover:bg-neutral-800"}
                ${!isSelected && isToday ? "ring-1 ring-[#F1C84B]/60" : ""}
                ${!isSelected && isFuture && c.inMonth ? "italic" : ""}
              `}
              aria-label={`Select ${key}${isToday ? " (today)" : ""}${isFuture ? " (future)" : ""}`}
            >
              <span className="leading-none">{c.d}</span>
              {dots.length > 0 && (
                <span className="absolute bottom-1 flex gap-0.5" data-testid={`log-calendar-dots-${key}`}>
                  {dots.slice(0, 3).map((s) => (
                    <span
                      key={s}
                      className="w-1 h-1 rounded-full"
                      style={{ background: STREAM_COLORS[s] || "#F1C84B" }}
                    />
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Footer — today btn + legend */}
      <div className="mt-2 flex items-center justify-between text-[10px] font-mono">
        <button
          onClick={gotoToday}
          className="px-2 py-0.5 rounded border border-neutral-600 text-neutral-300 hover:border-[#F1C84B] hover:text-[#F1C84B] transition-colors"
          data-testid="log-calendar-today-btn"
        >
          Today
        </button>
        <div className="flex items-center gap-2">
          {streams.map((s) => (
            <span key={s} className="flex items-center gap-1 text-neutral-400">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: STREAM_COLORS[s] }}
              />
              {STREAM_LABELS[s]}
            </span>
          ))}
        </div>
      </div>
      {selectedDate && selectedDate !== today && (
        <div
          className="mt-2 text-center text-[10px] font-mono text-[#F1C84B]"
          data-testid="log-calendar-selected-note"
        >
          {selectedDate > today
            ? `📌 Planning ahead — ${selectedDate}`
            : `📖 Viewing past — ${selectedDate}`}
        </div>
      )}
    </div>
  );
}

// Expose PT today for callers
export function todayKeyPTHelper() {
  return todayKeyPT();
}
